// Can the cast screen put this ELECTION to a member? One answer, read in two places.
//
// The cast screen (`cast/CastPage.tsx`) marks an election with a radio group, and a
// radio group expresses exactly one place counted first-past-the-post. Two surfaces
// ask that question and must never disagree about the answer:
//
//  • CastPage's `inspect` REFUSES a paper it cannot put — loud and closed, and with
//    the reason, because the Synod Admin's repair differs between the two;
//  • the ballot builder (`setup/BallotBuilder.tsx`) shows an inline "setup is ahead
//    of the voting screen" note on an election the cast screen would refuse — a
//    note, never a block: the draft is lawful and creatable.
//
// The rule used to be written in both files with a "must match" comment between
// them, and the builder's copy had already drifted once (it lacked the
// alternate-places term until P3a-2). So it is written here, once, and both import
// it (cca-89su).
//
// WHAT IS NOT HERE: whether the columns can be READ at all. A NULL `seats` or
// `alternateSeats`, or an electorate this build cannot name, is CastPage's own
// `unreadable` refusal, checked before and after this rule; the builder's typed
// draft cannot carry either shape. Only the votability rule is shared.

/** The three columns the rule reads. `method` is the wire's plain word (NULL off
 *  the wire is possible); the builder's narrower `'fptp' | 'approval'` fits it. */
export interface PositionVotabilityShape {
  method: string | null;
  seats: number;
  alternateSeats: number;
}

/** Why the cast screen cannot put an election to a member. The words are
 *  CastPage's own refusal reasons, so its `inspect` passes them straight through. */
export type PositionNotVotableReason = 'multi-seat' | 'method';

/**
 * Why the cast screen cannot put this election to a member, or `null` when it can.
 *
 * THE ORDER IS THE CAST SCREEN'S, and it is part of the answer: places first, then
 * method — so an election that both fills two places and is counted by approval is
 * refused as `multi-seat`.
 */
export function positionNotVotableInApp({
  method,
  seats,
  alternateSeats,
}: PositionVotabilityShape): PositionNotVotableReason | null {
  // seats + alternateSeats is the number of places filled from ONE ballot (SO 15.10
  // + the Election of a Bishop Canon cl 5), and it is the number a radio group can
  // and cannot express — not `seats` alone. A builder's emptied seats field is NaN,
  // which is not 1, so it lands here too.
  if (seats + alternateSeats !== 1) return 'multi-seat';
  // Covers NULL and every word but `fptp`, since `approval` and `stv` each need a
  // different control AND a different guard. Neither is approximated.
  if (method !== 'fptp') return 'method';
  return null;
}

/** Whether a member can vote on this election in the app today — the builder's
 *  question. Derived from {@link positionNotVotableInApp}, so the two cannot
 *  disagree. */
export function positionVotableInApp(shape: PositionVotabilityShape): boolean {
  return positionNotVotableInApp(shape) === null;
}
